import React, { useCallback, useEffect, useState } from 'react';
import { Maximize2, Minimize2, X } from 'lucide-react';
interface FullscreenPanelProps {
  title: string;
  children: React.ReactNode;
  /** Extra actions to show in fullscreen header (e.g. RefreshButton) */
  headerActions?: React.ReactNode;
}
export function FullscreenPanel({
  title,
  children,
  headerActions
}: FullscreenPanelProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const close = useCallback(() => setIsFullscreen(false), []);
  // Close on Escape key
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isFullscreen, close]);
  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  return <>
      {/* Expand button — rendered inline where the component is used */}
      <button onClick={() => setIsFullscreen(true)} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all" title="Pantalla completa" data-id="element-2118">
        <Maximize2 size={14} data-id="element-2119" />
      </button>

      {/* Fullscreen Overlay */}
      {isFullscreen && <div className="fixed inset-0 z-[100] flex flex-col animate-in fade-in duration-200" data-id="element-2120">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-sm" onClick={close} data-id="element-2121" />

          {/* Panel */}
          <div className="relative z-10 flex flex-col h-full m-4 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" data-id="element-2122">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900 shrink-0" data-id="element-2123">
              <h2 className="text-lg font-bold text-slate-100" data-id="element-2124">{title}</h2>
              <div className="flex items-center gap-2" data-id="element-2125">
                {headerActions}
                <button onClick={close} className="p-2 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-all" title="Cerrar (Esc)" data-id="element-2126">
                  <Minimize2 size={18} data-id="element-2127" />
                </button>
              </div>
            </div>

            {/* Content — fills remaining space */}
            <div className="flex-1 overflow-auto p-6" data-id="element-2128">{children}</div>
          </div>
        </div>}
    </>;
}
/**
 * Convenience wrapper: renders a Card header with title + fullscreen button + optional actions.
 * Use inside a Card to get consistent header with expand capability.
 */
export function CardHeaderWithFullscreen({
  title,
  children,
  headerActions
}: {
  title: string;
  children: React.ReactNode;
  headerActions?: React.ReactNode;
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const close = useCallback(() => setIsFullscreen(false), []);
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isFullscreen, close]);
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  return <>
      {/* Inline header row */}
      <div className="flex items-center justify-between mb-4" data-id="element-2129">
        <h3 className="font-bold text-slate-100" data-id="element-2130">{title}</h3>
        <div className="flex items-center gap-1" data-id="element-2131">
          {headerActions}
          <button onClick={() => setIsFullscreen(true)} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all" title="Pantalla completa" data-id="element-2132">
            <Maximize2 size={14} data-id="element-2133" />
          </button>
        </div>
      </div>

      {/* Normal inline content */}
      {children}

      {/* Fullscreen Overlay */}
      {isFullscreen && <div className="fixed inset-0 z-[100] flex flex-col animate-in fade-in duration-200" data-id="element-2134">
          <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-sm" onClick={close} data-id="element-2135" />

          <div className="relative z-10 flex flex-col h-full m-4 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" data-id="element-2136">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900 shrink-0" data-id="element-2137">
              <h2 className="text-lg font-bold text-slate-100" data-id="element-2138">{title}</h2>
              <div className="flex items-center gap-2" data-id="element-2139">
                {headerActions}
                <button onClick={close} className="p-2 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-all" title="Cerrar (Esc)" data-id="element-2140">
                  <Minimize2 size={18} data-id="element-2141" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6" data-id="element-2142">{children}</div>
          </div>
        </div>}
    </>;
}